// Operator 2026-10-18: "an operator can add their details" — every signed-in
// person (admin or not; Settings is admin-only) sets their own name here, from
// the name chip in the header. That name is what sign-offs, audit records and
// the client's Excel file show (PATCH /operators/me).
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "../auth/AuthContext";
import { updateMyName } from "../lib/authApi";
import { errorMessage } from "../lib/errorMessage";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";

const ROLE_LABEL = { ADMIN: "Admin", OPERATOR: "Operator" };

export function MyNameChip() {
  const { admin, refresh } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!admin) return null;
  const shown = admin.display_name || (admin.email || "").split("@")[0];
  const start = () => { setName(admin.display_name || ""); setError(null); setOpen(true); };
  const save = async () => {
    setBusy(true); setError(null);
    try {
      await updateMyName(name.trim());
      await refresh();
      setOpen(false);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" data-testid="header-user-chip" onClick={start}
        title={t("ui.header.my_name.chip_title", { email: admin.email, role: ROLE_LABEL[admin.role] || admin.role, defaultValue: `${admin.email} · ${ROLE_LABEL[admin.role] || admin.role} — change your name` })}
        className="border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 hover:border-teal-400 dark:hover:border-teal-500 px-3 py-1 rounded-full text-slate-700 dark:text-slate-200 text-xs font-medium flex items-center gap-1.5 transition-colors">
        {shown}
        <span className="text-teal-700 dark:text-teal-300"> · {admin.role}</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="my-name-dialog">
          <DialogHeader>
            <DialogTitle>{t("ui.header.my_name.title", { defaultValue: "Your name" })}</DialogTitle>
            <DialogDescription>
              {t("ui.header.my_name.description", {
                email: admin.email,
                defaultValue: `This is the name shown on your sign-offs, in the audit records and in the client's Excel file. Your login stays ${admin.email}.`,
              })}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) save(); }}>
            <input data-testid="my-name-input" aria-label={t("ui.header.my_name.title", { defaultValue: "Your name" })} autoComplete="name" autoFocus
              value={name} onChange={(e) => setName(e.target.value)} placeholder={t("ui.header.my_name.placeholder", { defaultValue: "Full name…" })}
              className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-3 py-2 text-base sm:text-sm rounded-xl text-slate-900 dark:text-slate-50" />
            {error && <div role="alert" data-testid="my-name-error" className="mt-2 text-xs text-red-700 dark:text-red-300">{typeof error === "string" ? error : JSON.stringify(error)}</div>}
            <DialogFooter className="mt-4">
              <button type="button" onClick={() => setOpen(false)}
                className="px-3 py-1.5 text-xs rounded-xl border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200">{t("ui.freeze.confirm.cancel", { defaultValue: "Cancel" })}</button>
              <button type="submit" data-testid="my-name-save" disabled={busy || !name.trim()}
                className="px-3 py-1.5 text-xs rounded-xl bg-teal-600 text-white font-semibold disabled:opacity-50">
                {busy ? t("ui.header.my_name.saving", { defaultValue: "Saving…" }) : t("ui.header.my_name.save", { defaultValue: "Save name" })}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
